import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { getChatClient } from '@/lib/ably'
import { Room, Message as AblyMessage } from '@ably/chat'

interface Message {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  created_at: string
  read_at?: string
  sender: {
    id: string
    display_name: string | null
    full_name: string | null
    avatar_url: string | null
  }
}

export function useConversation(conversationId: string) {
  const [room, setRoom] = useState<Room | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Load message history from Supabase with pagination
  const loadHistory = useCallback(async (offset = 0, limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, display_name, full_name, avatar_url)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })
        .range(offset, offset + limit - 1)

      if (error) throw error
      
      if (offset === 0) {
        setMessages(data || [])
      } else {
        setMessages(prev => [...(data || []), ...prev])
      }
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  // Send message (dual-write pattern)
  const sendMessage = useCallback(async (content: string) => {
    if (!room) return

    // Input validation and sanitization
    const sanitizedContent = content.trim()
    if (!sanitizedContent) return
    if (sanitizedContent.length > 5000) {
      throw new Error('Message too long (max 5000 characters)')
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      // 1. First, save to Supabase for persistence
      const { data: savedMessage, error: dbError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: sanitizedContent
        })
        .select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, display_name, full_name, avatar_url)
        `)
        .single()

      if (dbError) throw dbError

      // 2. Then send via Ably for real-time delivery
      await room.messages.send({
        text: sanitizedContent,
        metadata: {
          supabase_id: savedMessage.id,
          sender_name: savedMessage.sender.display_name || savedMessage.sender.full_name || 'Anonymous',
          sender_avatar: savedMessage.sender.avatar_url
        }
      })

      // 3. Update local state
      setMessages(prev => [...prev, savedMessage])

    } catch (err) {
      setError(err as Error)
      throw err
    }
  }, [room, conversationId])

  // Mark messages as read
  const markAsRead = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      await supabase.rpc('mark_messages_read', {
        p_conversation_id: conversationId,
        p_user_id: user.id
      })
    } catch (err) {
      console.error('Failed to mark as read:', err)
    }
  }, [conversationId])

  useEffect(() => {
    loadHistory()

    // Set up Ably Chat room
    let mounted = true
    let currentRoom: Room | null = null

    async function setupRoom() {
      try {
        const chatClient = await getChatClient()
        const roomInstance = await chatClient.rooms.get(`conversation:${conversationId}`)
        
        if (mounted) {
          currentRoom = roomInstance
          setRoom(roomInstance)

          // Subscribe to new messages
          const { unsubscribe } = roomInstance.messages.subscribe((message) => {
            // Only add if it's a new message (not in our history)
            const supabaseId = message.metadata?.supabase_id
            if (supabaseId && !messages.some(m => m.id === supabaseId)) {
              const newMessage: Message = {
                id: supabaseId,
                conversation_id: conversationId,
                sender_id: message.clientId,
                content: message.text,
                created_at: new Date(message.timestamp).toISOString(),
                sender: {
                  id: message.clientId,
                  display_name: message.metadata?.sender_name || 'Unknown',
                  full_name: message.metadata?.sender_name || 'Unknown',
                  avatar_url: message.metadata?.sender_avatar
                }
              }
              setMessages(prev => [...prev, newMessage])
            }
          })

          // Attach to room (enables presence, typing, etc.)
          await roomInstance.attach()
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error)
        }
      }
    }

    setupRoom()

    return () => {
      mounted = false
      if (currentRoom) {
        currentRoom.detach()
      }
    }
  }, [conversationId, loadHistory])

  const loadMoreMessages = useCallback(async () => {
    if (messages.length > 0) {
      await loadHistory(messages.length, 50)
    }
  }, [messages.length, loadHistory])

  return {
    messages,
    loading,
    error,
    sendMessage,
    markAsRead,
    loadMoreMessages,
    room
  }
}
